import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, type ReactNode } from "react";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import {
  TrendingUp,
  Tag,
  Calendar,
  X,
  History,
  Settings,
  RefreshCw,
  Clock,
  Package,
} from "lucide-react";

interface PricingConfig {
  baseMultiplier: number;
  conditionWeights: {
    excellent: number;
    likeNew: number;
    good: number;
    fair: number;
  };
  demandAdjustment: boolean;
  seasonalAdjustment: boolean;
  categoryPremiums: { category: string; premium: number }[];
}

interface MarketRefreshConfig {
  enabled: boolean;
  frequency: "hourly" | "daily" | "weekly" | "monthly";
  lastRun: string | null;
  nextRun: string | null;
  productsMonitored: number;
  categories: string[];
  priceThresholdAlert: number; // percentage change to trigger alert
}

interface PricingTrend {
  date: string;
  avgPrice: number;
  volume: number;
  margin: number;
}

interface CategoryPricing {
  category: string;
  avgTradeIn: number;
  avgResale: number;
  margin: number;
  volume: number;
}

export const Route = createFileRoute("/pricing-engine")({
  component: PricingEngineDashboard,
});

function PricingEngineDashboard() {
  const { currentTenant } = useTenant();
  const [config, setConfig] = useState<PricingConfig | null>(null);
  const [trends, setTrends] = useState<PricingTrend[]>([]);
  const [categoryPricing, setCategoryPricing] = useState<CategoryPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showRulesConfig, setShowRulesConfig] = useState(false);
  const [showMarketRefreshConfig, setShowMarketRefreshConfig] = useState(false);
  const [marketRefreshConfig, setMarketRefreshConfig] =
    useState<MarketRefreshConfig>(getDefaultMarketRefreshConfig());

  useEffect(() => {
    loadPricingData();
  }, [currentTenant.id]);

  const loadPricingData = async () => {
    setLoading(true);
    try {
      const res = await tenantFetch("/api/v1/pricing/config");
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
        setTrends(data.trends || []);
        setCategoryPricing(data.categories || []);
      } else {
        // Demo data
        setConfig(getDemoConfig());
        setTrends(getDemoTrends());
        setCategoryPricing(getDemoCategoryPricing());
      }
    } catch (error) {
      console.error("Failed to load pricing data:", error);
      setConfig(getDemoConfig());
      setTrends(getDemoTrends());
      setCategoryPricing(getDemoCategoryPricing());
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: currentTenant.color,
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
            Market Resale Intelligence
          </h1>
          <p className="text-sm mt-1" style={{ color: "#737373" }}>
            Market-driven pricing for ReBird trade-ins and resale
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowHistory(true)}
            className="px-4 py-2 text-sm font-light border rounded-full hover:bg-gray-50 transition-colors flex items-center gap-2"
            style={{ borderColor: "#E5E5E5", color: "#525252" }}
          >
            <History size={16} />
            View History
          </button>
          <button
            onClick={() => setShowMarketRefreshConfig(true)}
            className="px-4 py-2 text-sm font-light border rounded-full hover:bg-gray-50 transition-colors flex items-center gap-2"
            style={{ borderColor: "#E5E5E5", color: "#525252" }}
          >
            <RefreshCw size={16} />
            Market Refresh
          </button>
          <button
            onClick={() => setShowRulesConfig(true)}
            className="px-4 py-2 text-sm font-light text-white rounded-full hover:opacity-90 transition-opacity flex items-center gap-2"
            style={{ backgroundColor: currentTenant.color }}
          >
            <Settings size={16} />
            Configure Rules
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Avg Trade-In Value"
          value="$127"
          change="+12%"
          positive
          color={currentTenant.color}
        />
        <MetricCard
          label="Avg Resale Price"
          value="$198"
          change="+8%"
          positive
          color="#00FBA9"
        />
        <MetricCard
          label="Avg Margin"
          value="35.8%"
          change="+2.4%"
          positive
          color="#3B82F6"
        />
        <MetricCard
          label="Price Accuracy"
          value="94.2%"
          change="+1.8%"
          positive
          color="#8B5CF6"
        />
      </div>

      {/* How Dynamic Pricing Works */}
      <div className=" p-6" style={{ backgroundColor: "#171717" }}>
        <h2 className="text-lg font-light text-white mb-4">
          How Dynamic Pricing Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <PricingFactor
            icon={<TrendingUp className="w-8 h-8" />}
            title="Market Demand"
            description="Real-time demand signals from resale marketplace activity, sell-through rates, and search trends"
            weight={0.35}
          />
          <PricingFactor
            icon={<Tag className="w-8 h-8" />}
            title="Product Category"
            description="Category-specific premiums based on historical resale performance across Shell, Insulated, and Softshell"
            weight={0.30}
          />
          <PricingFactor
            icon={<Calendar className="w-8 h-8" />}
            title="Seasonality"
            description="Seasonal adjustments for alpine gear — winter shell jackets command a premium October through March"
            weight={0.20}
          />
          <PricingFactor
            icon={<Settings className="w-8 h-8" />}
            title="Arc'teryx Pricing Rules"
            description="Brand-defined guardrails: minimum payout floors, category overrides, and margin protection thresholds"
            weight={0.15}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Price Trends */}
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            Pricing Trends (30 Days)
          </h2>
          <div className="h-64">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={100}
              minHeight={100}
            >
              <AreaChart data={trends}>
                <defs>
                  <linearGradient
                    id="priceGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={currentTenant.color}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={currentTenant.color}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  stroke="#737373"
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="#737373"
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  formatter={(value) => [`$${value ?? 0}`, "Avg Price"]}
                  contentStyle={{
                    backgroundColor: "#171717",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#737373" }}
                  itemStyle={{ color: "#fff" }}
                />
                <Area
                  type="monotone"
                  dataKey="avgPrice"
                  stroke={currentTenant.color}
                  strokeWidth={2}
                  fill="url(#priceGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Performance */}
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            Category Pricing Performance
          </h2>
          <div className="h-64">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={100}
              minHeight={100}
            >
              <BarChart data={categoryPricing} layout="vertical">
                <XAxis
                  type="number"
                  tick={{ fontSize: 12 }}
                  stroke="#737373"
                  tickFormatter={(v) => `$${v}`}
                />
                <YAxis
                  dataKey="category"
                  type="category"
                  tick={{ fontSize: 12 }}
                  stroke="#737373"
                  width={100}
                />
                <Tooltip
                  formatter={(value) => [`$${value ?? 0}`, ""]}
                  contentStyle={{
                    backgroundColor: "#171717",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#737373" }}
                  itemStyle={{ color: "#fff" }}
                />
                <Bar
                  dataKey="avgTradeIn"
                  fill="#00FBA9"
                  name="Trade-In Value"
                  radius={[0, 4, 4, 0]}
                />
                <Bar
                  dataKey="avgResale"
                  fill={currentTenant.color}
                  name="Resale Price"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Pricing Configuration */}
      {config && (
        <div
          className=" p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-light" style={{ color: "#171717" }}>
              Pricing Configuration
            </h2>
            <span
              className="text-xs px-3 py-1 rounded-full"
              style={{ backgroundColor: "#DCFCE7", color: "#166534" }}
            >
              Active
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ConfigCard
              title="Arc'teryx Pricing Rules"
              items={[
                { label: "Min payout (Shell)", value: "$80" },
                { label: "Min payout (Insulated)", value: "$50" },
                { label: "Max payout cap", value: "$300" },
                { label: "Margin floor", value: "25%" },
              ]}
            />
            <ConfigCard
              title="Category Premiums"
              items={config.categoryPremiums.slice(0, 4).map((cp) => ({
                label: cp.category,
                value: cp.premium > 0 ? `+${cp.premium}%` : `${cp.premium}%`,
              }))}
            />
            <ConfigCard
              title="Demand Adjustment"
              items={[
                {
                  label: "Status",
                  value: config.demandAdjustment ? "Enabled" : "Disabled",
                },
                { label: "Max Boost", value: "+15%" },
                { label: "Min Reduction", value: "-10%" },
              ]}
            />
            <ConfigCard
              title="Seasonal Rules"
              items={[
                {
                  label: "Status",
                  value: config.seasonalAdjustment ? "Enabled" : "Disabled",
                },
                { label: "Winter Gear", value: "+20%" },
                { label: "Summer Gear", value: "-5%" },
              ]}
            />
          </div>
        </div>
      )}

      {/* Market Refresh Status */}
      <div
        className=" p-6"
        style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-light" style={{ color: "#171717" }}>
            Market Price Monitoring
          </h2>
          <div className="flex items-center gap-2">
            {marketRefreshConfig.enabled ? (
              <span
                className="text-xs px-3 py-1 rounded-full flex items-center gap-1"
                style={{ backgroundColor: "#DCFCE7", color: "#166534" }}
              >
                <RefreshCw
                  size={12}
                  className="animate-spin"
                  style={{ animationDuration: "3s" }}
                />
                Active
              </span>
            ) : (
              <span
                className="text-xs px-3 py-1 rounded-full"
                style={{ backgroundColor: "#FEE2E2", color: "#991B1B" }}
              >
                Paused
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div
            className="bg-white p-4 border"
            style={{ borderColor: "#E5E5E5" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} style={{ color: "#737373" }} />
              <span
                className="text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Refresh Frequency
              </span>
            </div>
            <p
              className="text-lg font-light capitalize"
              style={{ color: "#171717" }}
            >
              {marketRefreshConfig.frequency}
            </p>
          </div>

          <div
            className="bg-white p-4 border"
            style={{ borderColor: "#E5E5E5" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Package size={16} style={{ color: "#737373" }} />
              <span
                className="text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Products Monitored
              </span>
            </div>
            <p className="text-lg font-light" style={{ color: "#171717" }}>
              {marketRefreshConfig.productsMonitored.toLocaleString()}
            </p>
          </div>

          <div
            className="bg-white p-4 border"
            style={{ borderColor: "#E5E5E5" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <History size={16} style={{ color: "#737373" }} />
              <span
                className="text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Last Refresh
              </span>
            </div>
            <p className="text-lg font-light" style={{ color: "#171717" }}>
              {marketRefreshConfig.lastRun
                ? new Date(marketRefreshConfig.lastRun).toLocaleString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )
                : "Never"}
            </p>
          </div>

          <div
            className="bg-white p-4 border"
            style={{ borderColor: "#E5E5E5" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw size={16} style={{ color: "#737373" }} />
              <span
                className="text-xs font-light uppercase"
                style={{ color: "#737373" }}
              >
                Next Refresh
              </span>
            </div>
            <p className="text-lg font-light" style={{ color: "#171717" }}>
              {marketRefreshConfig.nextRun
                ? new Date(marketRefreshConfig.nextRun).toLocaleString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )
                : "Not scheduled"}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {marketRefreshConfig.categories.map((cat, i) => (
              <span
                key={i}
                className="text-xs px-2 py-1 rounded-full"
                style={{ backgroundColor: "#E5E5E5", color: "#525252" }}
              >
                {cat}
              </span>
            ))}
          </div>
          <button
            onClick={() => setShowMarketRefreshConfig(true)}
            className="text-sm font-light underline"
            style={{ color: currentTenant.color }}
          >
            Configure
          </button>
        </div>
      </div>

      {/* AI Learning */}
      <div
        className=" p-6"
        style={{ backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12  flex items-center justify-center text-2xl"
            style={{ backgroundColor: "#00FBA9" }}
          >
            🧠
          </div>
          <div>
            <h3
              className="text-lg font-light mb-2"
              style={{ color: "#166534" }}
            >
              Continuous Learning
            </h3>
            <p className="text-sm" style={{ color: "#15803D" }}>
              The pricing engine learns from every transaction. Over the past 30
              days, it has improved price accuracy by 4.2% and increased average
              margins by $12 per item. The model analyzes 50+ signals including
              marketplace trends, competitor pricing, and customer behavior to
              optimize every offer.
            </p>
          </div>
        </div>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: "#E5E5E5" }}
            >
              <h2 className="text-lg font-light" style={{ color: "#171717" }}>
                Pricing History
              </h2>
              <button
                onClick={() => setShowHistory(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} style={{ color: "#737373" }} />
              </button>
            </div>
            <div
              className="p-4 overflow-y-auto"
              style={{ maxHeight: "calc(80vh - 60px)" }}
            >
              <div className="space-y-3">
                {getPricingHistory().map((entry, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3"
                    style={{ backgroundColor: "#FAFAFA" }}
                  >
                    <div>
                      <p
                        className="text-sm font-medium"
                        style={{ color: "#171717" }}
                      >
                        {entry.change}
                      </p>
                      <p className="text-xs" style={{ color: "#737373" }}>
                        {entry.date}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-sm"
                        style={{
                          color: entry.impact > 0 ? "#16A34A" : "#DC2626",
                        }}
                      >
                        {entry.impact > 0 ? "+" : ""}
                        {entry.impact}% margin
                      </p>
                      <p className="text-xs" style={{ color: "#737373" }}>
                        {entry.user}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configure Rules Modal */}
      {showRulesConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-xl w-full max-h-[80vh] overflow-hidden">
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: "#E5E5E5" }}
            >
              <h2 className="text-lg font-light" style={{ color: "#171717" }}>
                Configure Pricing Rules
              </h2>
              <button
                onClick={() => setShowRulesConfig(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} style={{ color: "#737373" }} />
              </button>
            </div>
            <div
              className="p-4 overflow-y-auto"
              style={{ maxHeight: "calc(80vh - 120px)" }}
            >
              <div className="space-y-6">
                {/* Base Multiplier */}
                <div>
                  <label
                    className="block text-sm mb-2"
                    style={{ color: "#171717" }}
                  >
                    Base Multiplier
                  </label>
                  <input
                    type="range"
                    min="0.3"
                    max="0.8"
                    step="0.05"
                    defaultValue={config?.baseMultiplier || 0.6}
                    className="w-full"
                  />
                  <div
                    className="flex justify-between text-xs mt-1"
                    style={{ color: "#737373" }}
                  >
                    <span>30%</span>
                    <span>
                      {((config?.baseMultiplier || 0.6) * 100).toFixed(0)}%
                    </span>
                    <span>80%</span>
                  </div>
                </div>

                {/* Toggle Options */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm" style={{ color: "#171717" }}>
                        Demand Adjustment
                      </p>
                      <p className="text-xs" style={{ color: "#737373" }}>
                        Auto-adjust based on market demand
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        defaultChecked={config?.demandAdjustment}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-green-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm" style={{ color: "#171717" }}>
                        Seasonal Adjustment
                      </p>
                      <p className="text-xs" style={{ color: "#737373" }}>
                        Adjust prices seasonally
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        defaultChecked={config?.seasonalAdjustment}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-green-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                  </div>
                </div>

                {/* Condition Weights */}
                <div>
                  <p className="text-sm mb-3" style={{ color: "#171717" }}>
                    Condition Weights
                  </p>
                  <div className="space-y-2">
                    {["Excellent", "Like New", "Good", "Fair"].map(
                      (condition) => (
                        <div
                          key={condition}
                          className="flex items-center justify-between"
                        >
                          <span
                            className="text-sm"
                            style={{ color: "#737373" }}
                          >
                            {condition}
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            defaultValue={
                              condition === "Excellent"
                                ? 95
                                : condition === "Like New"
                                  ? 85
                                  : condition === "Good"
                                    ? 70
                                    : 50
                            }
                            className="w-20 px-2 py-1 border rounded text-sm text-center"
                            style={{ borderColor: "#E5E5E5" }}
                          />
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div
              className="flex justify-end gap-2 p-4 border-t"
              style={{ borderColor: "#E5E5E5" }}
            >
              <button
                onClick={() => setShowRulesConfig(false)}
                className="px-4 py-2 text-sm border rounded-full hover:bg-gray-50"
                style={{ borderColor: "#E5E5E5", color: "#525252" }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Would save config here
                  setShowRulesConfig(false);
                }}
                className="px-4 py-2 text-sm text-white rounded-full hover:opacity-90"
                style={{ backgroundColor: currentTenant.color }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Market Refresh Configuration Modal */}
      {showMarketRefreshConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: "#E5E5E5" }}
            >
              <div>
                <h2 className="text-lg font-light" style={{ color: "#171717" }}>
                  Market Price Refresh Configuration
                </h2>
                <p className="text-xs mt-1" style={{ color: "#737373" }}>
                  Configure how often AI checks resale market prices
                </p>
              </div>
              <button
                onClick={() => setShowMarketRefreshConfig(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} style={{ color: "#737373" }} />
              </button>
            </div>
            <div
              className="p-6 overflow-y-auto"
              style={{ maxHeight: "calc(80vh - 140px)" }}
            >
              <div className="space-y-6">
                {/* Enable/Disable */}
                <div
                  className="flex items-center justify-between p-4"
                  style={{ backgroundColor: "#FAFAFA" }}
                >
                  <div>
                    <p
                      className="text-sm font-medium"
                      style={{ color: "#171717" }}
                    >
                      Enable Market Monitoring
                    </p>
                    <p className="text-xs" style={{ color: "#737373" }}>
                      Automatically check resale market prices for your products
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={marketRefreshConfig.enabled}
                      onChange={(e) =>
                        setMarketRefreshConfig((prev) => ({
                          ...prev,
                          enabled: e.target.checked,
                        }))
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-green-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>

                {/* Refresh Frequency */}
                <div>
                  <label
                    className="block text-sm font-medium mb-3"
                    style={{ color: "#171717" }}
                  >
                    Refresh Frequency
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {(["hourly", "daily", "weekly", "monthly"] as const).map(
                      (freq) => (
                        <button
                          key={freq}
                          onClick={() =>
                            setMarketRefreshConfig((prev) => ({
                              ...prev,
                              frequency: freq,
                            }))
                          }
                          className={`px-4 py-3 text-sm border rounded-full transition-colors capitalize ${
                            marketRefreshConfig.frequency === freq
                              ? "border-2 font-medium"
                              : "border hover:bg-gray-50"
                          }`}
                          style={{
                            borderColor:
                              marketRefreshConfig.frequency === freq
                                ? currentTenant.color
                                : "#E5E5E5",
                            color:
                              marketRefreshConfig.frequency === freq
                                ? currentTenant.color
                                : "#525252",
                            backgroundColor:
                              marketRefreshConfig.frequency === freq
                                ? `${currentTenant.color}10`
                                : "white",
                          }}
                        >
                          {freq}
                        </button>
                      ),
                    )}
                  </div>
                  <p className="text-xs mt-2" style={{ color: "#737373" }}>
                    {marketRefreshConfig.frequency === "hourly" &&
                      "Recommended for high-volume products with volatile pricing"}
                    {marketRefreshConfig.frequency === "daily" &&
                      "Good balance between accuracy and API costs"}
                    {marketRefreshConfig.frequency === "weekly" &&
                      "Suitable for stable product categories"}
                    {marketRefreshConfig.frequency === "monthly" &&
                      "Best for seasonal or legacy products"}
                  </p>
                </div>

                {/* Category Selection */}
                <div>
                  <label
                    className="block text-sm font-medium mb-3"
                    style={{ color: "#171717" }}
                  >
                    Product Categories to Monitor
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {getAvailableCategories(currentTenant.id).map(
                      (category) => (
                        <label
                          key={category}
                          className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                            marketRefreshConfig.categories.includes(category)
                              ? "border-2"
                              : "border hover:bg-gray-50"
                          }`}
                          style={{
                            borderColor:
                              marketRefreshConfig.categories.includes(category)
                                ? currentTenant.color
                                : "#E5E5E5",
                            backgroundColor:
                              marketRefreshConfig.categories.includes(category)
                                ? `${currentTenant.color}10`
                                : "white",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={marketRefreshConfig.categories.includes(
                              category,
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setMarketRefreshConfig((prev) => ({
                                  ...prev,
                                  categories: [...prev.categories, category],
                                }));
                              } else {
                                setMarketRefreshConfig((prev) => ({
                                  ...prev,
                                  categories: prev.categories.filter(
                                    (c) => c !== category,
                                  ),
                                }));
                              }
                            }}
                            className="sr-only"
                          />
                          <div
                            className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                              marketRefreshConfig.categories.includes(category)
                                ? "border-0"
                                : ""
                            }`}
                            style={{
                              backgroundColor:
                                marketRefreshConfig.categories.includes(
                                  category,
                                )
                                  ? currentTenant.color
                                  : "white",
                              borderColor: "#E5E5E5",
                            }}
                          >
                            {marketRefreshConfig.categories.includes(
                              category,
                            ) && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                          <span
                            className="text-sm"
                            style={{ color: "#171717" }}
                          >
                            {category}
                          </span>
                        </label>
                      ),
                    )}
                  </div>
                </div>

                {/* Price Threshold Alert */}
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: "#171717" }}
                  >
                    Price Change Alert Threshold
                  </label>
                  <p className="text-xs mb-3" style={{ color: "#737373" }}>
                    Get notified when market prices change by more than this
                    percentage
                  </p>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="5"
                      max="50"
                      step="5"
                      value={marketRefreshConfig.priceThresholdAlert}
                      onChange={(e) =>
                        setMarketRefreshConfig((prev) => ({
                          ...prev,
                          priceThresholdAlert: parseInt(e.target.value),
                        }))
                      }
                      className="flex-1"
                    />
                    <div
                      className="w-16 text-center py-2 rounded-full text-sm font-medium"
                      style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
                    >
                      {marketRefreshConfig.priceThresholdAlert}%
                    </div>
                  </div>
                </div>

                {/* Run Now Button */}
                <div
                  className="p-4"
                  style={{
                    backgroundColor: "#F0FDF4",
                    border: "1px solid #BBF7D0",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className="text-sm font-medium"
                        style={{ color: "#166534" }}
                      >
                        Manual Refresh
                      </p>
                      <p className="text-xs" style={{ color: "#15803D" }}>
                        Run a market price check immediately for selected
                        categories
                      </p>
                    </div>
                    <button
                      className="px-4 py-2 text-sm font-light text-white rounded-full hover:opacity-90 flex items-center gap-2"
                      style={{ backgroundColor: "#16A34A" }}
                      onClick={() => {
                        // Simulate running a refresh
                        setMarketRefreshConfig((prev) => ({
                          ...prev,
                          lastRun: new Date().toISOString(),
                        }));
                      }}
                    >
                      <RefreshCw size={14} />
                      Run Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="flex justify-end gap-2 p-4 border-t"
              style={{ borderColor: "#E5E5E5" }}
            >
              <button
                onClick={() => setShowMarketRefreshConfig(false)}
                className="px-4 py-2 text-sm border rounded-full hover:bg-gray-50"
                style={{ borderColor: "#E5E5E5", color: "#525252" }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Save config to API
                  console.log(
                    "Saving market refresh config:",
                    marketRefreshConfig,
                  );
                  setShowMarketRefreshConfig(false);
                }}
                className="px-4 py-2 text-sm text-white rounded-full hover:opacity-90"
                style={{ backgroundColor: currentTenant.color }}
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper for pricing history
function getPricingHistory() {
  return [
    {
      change: "Base multiplier increased from 55% to 60%",
      date: "Dec 15, 2024",
      impact: 2.3,
      user: "System (Auto)",
    },
    {
      change: "Shell Jackets premium increased to +15%",
      date: "Dec 10, 2024",
      impact: 1.8,
      user: "Admin",
    },
    {
      change: "Enabled seasonal adjustment for winter",
      date: "Dec 1, 2024",
      impact: 4.2,
      user: "Admin",
    },
    {
      change: "Fair condition weight reduced to 50%",
      date: "Nov 28, 2024",
      impact: -0.5,
      user: "System (Auto)",
    },
    {
      change: "Demand adjustment enabled",
      date: "Nov 15, 2024",
      impact: 3.1,
      user: "Admin",
    },
    {
      change: "Initial pricing rules configured",
      date: "Nov 1, 2024",
      impact: 0,
      user: "System",
    },
  ];
}

function MetricCard({
  label,
  value,
  change,
  positive,
  color,
}: {
  label: string;
  value: string;
  change: string;
  positive: boolean;
  color: string;
}) {
  return (
    <div
      className=" p-5"
      style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
    >
      <div
        className="text-xs font-light uppercase mb-2"
        style={{ color: "#737373" }}
      >
        {label}
      </div>
      <div className="flex items-end justify-between">
        <div className="text-2xl font-semibold" style={{ color }}>
          {value}
        </div>
        <div
          className="text-sm font-light"
          style={{ color: positive ? "#16A34A" : "#DC2626" }}
        >
          {change}
        </div>
      </div>
    </div>
  );
}

function PricingFactor({
  icon,
  title,
  description,
  weight,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  weight: number;
}) {
  return (
    <div className="text-center">
      <div className="flex justify-center mb-3 text-[#00FBA9]">{icon}</div>
      <h3 className="text-white font-light mb-2">{title}</h3>
      <p className="text-xs text-gray-400 mb-3">{description}</p>
      <div className="w-full bg-gray-700 rounded-full h-2">
        <div
          className="h-2 rounded-full"
          style={{ width: `${weight * 100}%`, backgroundColor: "#00FBA9" }}
        />
      </div>
      <div className="text-xs text-gray-400 mt-1">
        {(weight * 100).toFixed(0)}% weight
      </div>
    </div>
  );
}

function ConfigCard({
  title,
  items,
}: {
  title: string;
  items: { label: string; value: string }[];
}) {
  return (
    <div className="bg-white  p-4 border" style={{ borderColor: "#E5E5E5" }}>
      <h4 className="font-light text-sm mb-3" style={{ color: "#171717" }}>
        {title}
      </h4>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span style={{ color: "#737373" }}>{item.label}</span>
            <span className="font-light" style={{ color: "#171717" }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Demo data
function getDemoConfig(): PricingConfig {
  return {
    baseMultiplier: 0.6,
    conditionWeights: { excellent: 0.95, likeNew: 0.85, good: 0.7, fair: 0.5 },
    demandAdjustment: true,
    seasonalAdjustment: true,
    categoryPremiums: [
      { category: "Shell Jackets", premium: 15 },
      { category: "Insulated Jackets", premium: 10 },
      { category: "Pants", premium: 5 },
      { category: "Packs", premium: 0 },
      { category: "Accessories", premium: -5 },
    ],
  };
}

function getDemoTrends(): PricingTrend[] {
  const data: PricingTrend[] = [];
  for (let i = 30; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      avgPrice: 115 + Math.random() * 30 + (30 - i) * 0.4,
      volume: Math.floor(20 + Math.random() * 15),
      margin: 32 + Math.random() * 8,
    });
  }
  return data;
}

function getDemoCategoryPricing(): CategoryPricing[] {
  return [
    {
      category: "Shell Jackets",
      avgTradeIn: 145,
      avgResale: 285,
      margin: 49.1,
      volume: 52,
    },
    {
      category: "Insulated",
      avgTradeIn: 95,
      avgResale: 165,
      margin: 42.4,
      volume: 48,
    },
    {
      category: "Pants",
      avgTradeIn: 55,
      avgResale: 95,
      margin: 42.1,
      volume: 28,
    },
    {
      category: "Packs",
      avgTradeIn: 35,
      avgResale: 65,
      margin: 46.2,
      volume: 18,
    },
  ];
}

function getDefaultMarketRefreshConfig(): MarketRefreshConfig {
  const now = new Date();
  const nextRun = new Date(now);
  nextRun.setHours(nextRun.getHours() + 24);

  return {
    enabled: true,
    frequency: "daily",
    lastRun: new Date(now.getTime() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago
    nextRun: nextRun.toISOString(),
    productsMonitored: 247,
    categories: ["Shell Jackets", "Insulated Jackets", "Pants"],
    priceThresholdAlert: 15,
  };
}

function getAvailableCategories(tenantId: string): string[] {
  if (tenantId === "arcteryx") {
    return [
      "Shell Jackets",
      "Insulated Jackets",
      "Ski Jackets",
      "Pants",
      "Packs & Bags",
      "Footwear",
      "Accessories",
      "Base Layers",
    ];
  } else if (tenantId === "lululemon") {
    return [
      "Leggings",
      "Shorts",
      "Tops",
      "Sports Bras",
      "Hoodies & Sweatshirts",
      "Outerwear",
      "Accessories",
      "Bags",
    ];
  } else if (tenantId === "nespresso") {
    return [
      "Original Machines",
      "Vertuo Machines",
      "Milk Frothers",
      "Accessories",
      "Limited Editions",
    ];
  }

  return ["Category 1", "Category 2", "Category 3", "Category 4"];
}
